import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { API_BASE, PROFILE_IMG_BASE } from "@config/env";

const TYPE_MAP = {
  jobRoles: "jobRoles", skills: "skills", certifications: "certifications",
  languages: "languages", location: "location",
};

const AVAIL_OPTIONS = [
  { label: "Available", sublabel: "20–40 hrs/wk", value: "available", color: "#16A34A", bg: "var(--fc-green-light)", border: "var(--fc-green-border)" },
  { label: "Partial",   sublabel: "10–19 hrs/wk", value: "partial",   color: "#F59E0B", bg: "var(--fc-amber-light)", border: "var(--fc-amber-border)" },
  { label: "Busy",      sublabel: "< 10 hrs/wk",  value: "busy",      color: "#DC2626", bg: "var(--fc-red-light)",   border: "var(--fc-red-border)" },
];

const SECTION_ICONS = {
  "Availability":    "fa-clock",
  "Job Roles":       "fa-briefcase",
  "Skills":          "fa-bolt",
  "Certifications":  "fa-certificate",
  "Languages":       "fa-language",
  "Location":        "fa-location-dot",
};

function TagInput({ id, placeholder, type, tags = [], onChange }) {
  const [input,       setInput]       = useState("");
  const [open,        setOpen]        = useState(false);
  const [hi,          setHi]          = useState(-1);
  const [suggestions, setSuggestions] = useState([]);
  const timer = useRef();

  useEffect(() => {
    const q = input.trim().toLowerCase();
    if (q.length < 2) { setSuggestions([]); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const fd = new FormData();
        fd.append("type", TYPE_MAP[type] || type);
        fd.append("keyword", q);
        const res = await axios.post(`${API_BASE}get-autocomplete-result`, fd);
        const apiItems = res.data?.status === 1
          ? (res.data.results || []).map(r => r.name || r).filter(s => !tags.includes(s))
          : [];
        setSuggestions(apiItems.slice(0, 8));
      } catch {
        setSuggestions([]);
      }
    }, 400);
  }, [input, type, tags]);

  const add = (v) => {
    v = (typeof v === "string" ? v : v.name || "").trim();
    if (v && !tags.includes(v)) onChange([...tags, v]);
    setInput(""); setSuggestions([]); setOpen(false);
  };
  const remove = (t) => onChange(tags.filter(x => x !== t));

  return (
    <div style={{ position: "relative" }}>
      {/* Tags + input row */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 5, padding: "6px 8px", border: "1.5px solid var(--fc-border)", borderRadius: 9, background: "var(--fc-card-bg)", cursor: "text", minHeight: 38, transition: "border-color 0.15s" }}
        onClick={() => document.getElementById(id)?.focus()}
        onFocusCapture={e => e.currentTarget.style.borderColor = "var(--fc-primary)"}
        onBlurCapture={e => { const el = e.currentTarget; setTimeout(() => { if (el && !el.contains(document.activeElement)) el.style.borderColor = "var(--fc-border)"; }, 200); }}>
        {tags.map(t => (
          <span key={t} style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", background: "var(--fc-primary-light)", border: "1px solid var(--fc-primary-border)", borderRadius: 99, fontSize: 11, fontWeight: 600, color: "var(--fc-primary)" }}>
            {t}
            <button onClick={e => { e.stopPropagation(); remove(t); }} style={{ background: "none", border: "none", cursor: "pointer", color: "#60A5FA", padding: 0, fontSize: 10, lineHeight: 1, display: "flex" }}>
              <i className="fa-solid fa-xmark" />
            </button>
          </span>
        ))}
        <input id={id} value={input} placeholder={(tags||[]).length === 0 ? placeholder : ""}
          style={{ border: "none", outline: "none", fontSize: 12, color: "var(--fc-text-secondary)", background: "transparent", flexGrow: 1, minWidth: 80, padding: "1px 2px" }}
          onChange={e => { setInput(e.target.value); setOpen(true); setHi(-1); }}
          onFocus={() => setOpen(true)}
          onKeyDown={e => {
            if (e.key === "Enter")     { e.preventDefault(); hi >= 0 && suggestions[hi] ? add(suggestions[hi]) : input && add(input); }
            if (e.key === "Backspace"  && !input && (tags||[]).length) remove(tags[tags.length - 1]);
            if (e.key === "ArrowDown") setHi(h => Math.min(h + 1, suggestions.length - 1));
            if (e.key === "ArrowUp")   setHi(h => Math.max(h - 1, -1));
            if (e.key === "Escape")    setOpen(false);
          }}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          autoComplete="off" />
      </div>

      {/* Dropdown */}
      {open && suggestions.length > 0 && (
        <div className="fc-scroll-container" style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "var(--fc-card-bg)", border: "1px solid var(--fc-border)", borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.1)", zIndex: 200, maxHeight: 200, overflowY: "auto" }}>
          {suggestions.map((s, i) => (
            <div key={s} onMouseDown={() => add(s)}
              style={{ padding: "8px 12px", fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, background: i === hi ? "var(--fc-primary-light)" : "var(--fc-card-bg)", color: i === hi ? "var(--fc-primary)" : "var(--fc-text-secondary)", borderBottom: i < suggestions.length - 1 ? "1px solid var(--fc-border-light)" : "none" }}
              onMouseEnter={() => setHi(i)}>
              <i className="fa-solid fa-magnifying-glass" style={{ fontSize: 9, color: "var(--fc-text-subtle)" }} />
              {s}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FilterSection({ label, children, defaultOpen = true, count = 0 }) {
  const [open, setOpen] = useState(defaultOpen);
  const icon = SECTION_ICONS[label] || "fa-filter";
  return (
    <div style={{ borderBottom:"1px solid var(--fc-border-light)" }}>
      <button onClick={() => setOpen(!open)}
        style={{ width:"100%", display:"flex", alignItems:"center", justifyContent:"space-between", padding:"11px 0", background:"none", border:"none", cursor:"pointer", fontFamily:"inherit" }}>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <div style={{ width:26, height:26, borderRadius:7, background:"var(--fc-border-light)", display:"flex", alignItems:"center", justifyContent:"center" }}>
            <i className={`fa-solid ${icon}`} style={{ fontSize:11, color:"var(--fc-text-muted)" }} />
          </div>
          <span style={{ fontSize:13, fontWeight:600, color:"var(--fc-text-primary)" }}>{label}</span>
          {count > 0 && (
            <span style={{ fontSize:10, fontWeight:700, color:"#fff", background:"var(--fc-primary)", borderRadius:99, padding:"1px 7px", lineHeight:1.6 }}>{count}</span>
          )}
        </div>
        <i className={`fa-solid fa-chevron-${open?"up":"down"}`} style={{ fontSize:9, color:"var(--fc-text-subtle)" }} />
      </button>
      {open && <div style={{ paddingBottom:12 }}>{children}</div>}
    </div>
  );
}

export default function SearchFilterPanel({ filters = {jobRoles:[],skills:[],certifications:[],languages:[],location:[],availability:""}, onChange, onReset, totalResults }) {
  const set = (k, v) => onChange({ ...filters, [k]: v });
  const activeCount = [
    (filters.jobRoles||[]).length > 0, (filters.skills||[]).length > 0,
    (filters.certifications||[]).length > 0, (filters.languages||[]).length > 0,
    (filters.location||[]).length > 0, !!filters.availability,
  ].filter(Boolean).length;

  return (
    <div style={{ background: "var(--fc-card-bg)", border: "1px solid var(--fc-card-border)", borderRadius: 14, overflow: "hidden", boxShadow: "var(--fc-card-shadow)" }}>

      {/* Header */}
      <div style={{ padding: "14px 16px", borderBottom: "1px solid var(--fc-border-light)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <i className="fa-solid fa-sliders" style={{ color: "var(--fc-primary)", fontSize: 13 }} />
          <span style={{ fontSize: 13, fontWeight: 700, color: "var(--fc-text-primary)" }}>Filters</span>
          {activeCount > 0 && (
            <span style={{ fontSize: 10, fontWeight: 700, color: "#fff", background: "var(--fc-primary)", borderRadius: 99, padding: "2px 7px" }}>{activeCount}</span>
          )}
        </div>
        {activeCount > 0 && (
          <button onClick={onReset}
            style={{ fontSize: 11, fontWeight: 600, color: "var(--fc-red)", background: "none", border: "none", cursor: "pointer", padding: 0, fontFamily: "inherit" }}>
            Reset all
          </button>
        )}
      </div>

      {/* Body */}
      <div className="fc-scroll-container" style={{ padding: "0 16px", maxHeight: "calc(100vh - 200px)", overflowY: "auto" }}>

        {/* Availability */}
        <FilterSection label="Availability" count={filters.availability ? 1 : 0}>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {AVAIL_OPTIONS.map(opt => {
              const active = filters.availability === opt.value;
              return (
                <button key={opt.value}
                  onClick={() => set("availability", active ? "" : opt.value)}
                  style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 10px", borderRadius: 9, border: `1.5px solid ${active ? opt.border : "var(--fc-border)"}`, background: active ? opt.bg : "var(--fc-bg-light)", cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: opt.color }} />
                    <span style={{ fontSize: 12, fontWeight: 600, color: active ? opt.color : "var(--fc-text-secondary)" }}>{opt.label}</span>
                  </div>
                  <span style={{ fontSize: 10, color: active ? opt.color : "var(--fc-text-subtle)", fontWeight: 500 }}>{opt.sublabel}</span>
                </button>
              );
            })}
          </div>
        </FilterSection>

        {/* Job Roles */}
        <FilterSection label="Job Roles" count={(filters.jobRoles||[]).length}>
          <TagInput id="f-roles" placeholder="Type to search roles…" type="jobRoles"
            tags={filters.jobRoles||[]} onChange={v => set("jobRoles", v)} />
        </FilterSection>

        {/* Skills */}
        <FilterSection label="Skills" count={(filters.skills||[]).length}>
          <TagInput id="f-skills" placeholder="Type to search skills…" type="skills"
            tags={filters.skills||[]} onChange={v => set("skills", v)} />
        </FilterSection>

        {/* Certifications */}
        <FilterSection label="Certifications" count={(filters.certifications||[]).length} defaultOpen={false}>
          <TagInput id="f-certs" placeholder="Type to search…" type="certifications"
            tags={filters.certifications||[]} onChange={v => set("certifications", v)} />
        </FilterSection>

        {/* Languages */}
        <FilterSection label="Languages" count={(filters.languages||[]).length} defaultOpen={false}>
          <TagInput id="f-langs" placeholder="Type to search…" type="languages"
            tags={filters.languages||[]} onChange={v => set("languages", v)} />
        </FilterSection>

        {/* Location */}
        <FilterSection label="Location" count={(filters.location||[]).length} defaultOpen={false}>
          <TagInput id="f-locs" placeholder="Type to search…" type="location"
            tags={filters.location||[]} onChange={v => set("location", v)} />
        </FilterSection>
      </div>

      {/* Footer */}
      <div style={{ padding: "12px 16px", borderTop: "1px solid var(--fc-border-light)", background: "var(--fc-bg-light)" }}>
        <div style={{ fontSize: 12, color: "var(--fc-text-muted)", textAlign: "center" }}>
          <strong style={{ color: "var(--fc-text-primary)" }}>{totalResults}</strong> candidate{totalResults !== 1 ? "s" : ""} found
        </div>
      </div>
    </div>
  );
}
